import { Controller } from '@hotwired/stimulus';
import { fetchData } from '@shared/script/fetchData';
import { wishlistStore, wishlistRetryStore, wishlistRetryContext } from '@site-module/store/wishlist';
import type { StoreController } from '@store/types/storeController';
import { useStore } from '@store/index';
import { CustomEventNames, dispatchEvent } from '@shared/script/eventDispatcher';
import { updateDataLayer } from '@site-module/datalayer';

type ActionParams = {
    reloadOnSuccess: string;
    id: string;
    sendEndpoint: string;
    retrieveEndpoint: string;
    title?: string;
}

type WishlistAddResponseObject = {
    blnReload: boolean;
    blnResult: boolean;
    strReloadUrl: string;
    strHtml: string;
};

type WishlistAddResponseMap = {
    [key: number]: boolean;
};

export type WishlistAddResponse = WishlistAddResponseObject | WishlistAddResponseMap;

/**
 * Sanitizes the retry payload by filtering out non-string values.
 * @param entries - The FormData entries to sanitize.
 * @returns A record containing only string values.
 */
function sanitizeRetryPayload(entries: FormData): Record<string, string> {
    const output: Record<string, string> = {};
    for (const [key, value] of entries.entries()) {
        if (typeof value === 'string') {
            output[key] = value;
        }
    }
    return output;
}

/**
 * Type guard to check if the response is a WishlistAddResponseObject.
 * @param response - The response to check.
 * @returns True if the response is a WishlistAddResponseObject, false otherwise.
 */
export function isWishlistResponseObject(response: unknown): response is WishlistAddResponseObject {
    return (
        typeof response === 'object' &&
        response !== null &&
        'blnReload' in response &&
        'blnResult' in response &&
        'strReloadUrl' in response &&
        'strHtml' in response
    );
}

/**
 * Type guard to check if the response is a WishlistAddResponseMap.
 * @param response - The response to check.
 * @returns True if the response is a WishlistAddResponseMap, false otherwise.
 */
export function isWishlistResponseMap(response: unknown): response is WishlistAddResponseMap {
    return (
        typeof response === 'object' &&
        response !== null &&
        !isWishlistResponseObject(response)
    );
}

export default class WishlistAddButtonController extends Controller<HTMLElement> implements StoreController {
    static stores = [wishlistStore, wishlistRetryStore, wishlistRetryContext];

    static values = {
        id: String,
        inOrderContext: Boolean
    };

    static targets = ['link', 'button'];

    /**
     * Store follows Stimulus convention for values and targets.
     * We have access to this.<storeName>Value, this.on<storeName>Update()
     * and this.set<storeName>Value(value) props and methods.
     */
    declare wishlistValue: number[];
    declare setWishlistValue: (wishlist: number[]) => void;
    declare onWishlistUpdate: () => void;
    declare wishlistRetryValue: Record<string, string>;
    declare setWishlistRetryValue: (data: Record<string, string>) => void;
    declare setWishlistRetryContextValue: (context: string) => void;
    declare buttonTarget: HTMLButtonElement;
    declare linkTarget: HTMLAnchorElement;
    declare hasLinkTarget: boolean;
    declare idValue: string;
    declare inOrderContextValue: boolean;

    fetchFn: typeof fetchData = fetchData;

    /**
     * Connect the controller to the store and set the initial state.
     */
    connect(): void {
        useStore(this);
    }

    async addToWishlist(event: Event & { currentTarget: HTMLButtonElement, params: ActionParams}): Promise<void> {
        const { sendEndpoint, payload, reloadOnSuccess = false } = this.handleEventParams(event);

        const result = await this.fetchFn<WishlistAddResponse>(sendEndpoint, 'POST', { body: payload });

        // Response object corresponds to a redirect response from the AuthListener
        if (result.ok && isWishlistResponseObject(result.data)) {
            const formObj = sanitizeRetryPayload(payload);
            this.setWishlistRetryValue(formObj);
            // In the order context, basically on the smarty version of the button,
            // this will be true, we can retrieve the one shot cookie on reload
            // and direct the request to the correct BE endpoint.
            if (this.inOrderContextValue === true) {
                this.setWishlistRetryContextValue('order');
            }
            this.redirectTo(result.data.strReloadUrl);

            // Response map corresponds to a standard successful add response
            // We can the dispatch UI updates from here.
        } else if (result.ok && isWishlistResponseMap(result.data)) {
            this.setWishlistRetryValue({});
            this.buttonTarget.classList.remove('pending');

            Object.entries(result.data).forEach(([key, value]) => {
                if (value === true) {
                    this.setWishlistValue([...this.wishlistValue, Number(key)]);
                }
            });

            dispatchEvent(CustomEventNames.WishlistModalOpen, {
                modalId: 'wishlistModal',
                title: event.params?.title
            });

            if (reloadOnSuccess) {
                window.location.reload();
            }
        }

        this.updateDataLayer();
    }

    /**
     * Because of limitaions in jsdom, we must mock this in our tests
     * Rather than hitting it in the main flow above.
     *
     * @param path
     */
    redirectTo(path: string): void {
        window.location.href = path;
    }

    /**
     * Handle the event parameters and prepare the payload for the request.
     * @param event - The event object containing the current target and parameters.
     * @returns An object containing the send endpoint and payload.
     */
    handleEventParams(
        event: Event & { currentTarget: HTMLButtonElement, params: ActionParams }
    ): { sendEndpoint: string, payload: FormData, reloadOnSuccess: boolean } {
        event.preventDefault();
        event.stopPropagation();

        event.currentTarget?.classList?.add('pending');

        const {
            id,
            sendEndpoint,
            reloadOnSuccess,
            title = ''
        } = event.params;

        const payload = new FormData();

        payload.append('id', id);
        payload.append('title', title);

        return {
            sendEndpoint,
            payload,
            reloadOnSuccess
        };
    }

    /**
     * Subscribe to the wishlist update event to update the button and link visibility.
     * The fetch is triggered by the wishlist count controller, which sets the state.
     */
    onWishlistUpdate(): void {
        const id = Number(this.idValue);
        if (this.wishlistValue?.includes(id)) {
            this.buttonTarget?.classList.remove('pending');
            this.buttonTarget?.classList.add('hidden');
            if (this.hasLinkTarget) {
                this.linkTarget?.classList.remove('hidden');
            }
        }
    }

    updateDataLayer(): void {
        const button: HTMLButtonElement = this.element.querySelector('.btnAddWishList');
        updateDataLayer('add_to_wishlist', button.dataset);
    }
}
